import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ConfigurationService } from '../configuration.service';
import {
    AuthenticationResponseJSON,
    OrganizationMemberSecurityStatus,
    PasskeyAuthenticationOptionsResponse,
    PasskeyRegistrationOptionsResponse,
    RegistrationResponseJSON,
    StrongAuthLoginResponse,
    TotpEnrollmentResponse,
    UserSecurityCredentials,
} from '../../models/security/security.models';
import { OrganizationSecurityPolicy } from '../../models/organization/organization-model';

@Injectable({ providedIn: 'root' })
export class SecurityApiService {
    private readonly publicUrl: string;
    private readonly authenticatedUrl: string;

    constructor(
        private http: HttpClient,
        configuration: ConfigurationService
    ) {
        this.publicUrl = `${configuration.apiIP}/security`;
        this.authenticatedUrl = `${configuration.apiIP}/auth/security`;
    }

    public completeLogin$(continuation: string): Observable<StrongAuthLoginResponse> {
        return this.http.post<StrongAuthLoginResponse>(`${this.publicUrl}/login/status`, { continuation });
    }

    public resumeLogin$(continuation: string): Observable<StrongAuthLoginResponse> {
        return this.http.post<StrongAuthLoginResponse>(`${this.publicUrl}/login/resume`, { continuation });
    }

    public verifyEmailLoginCode$(
        continuation: string,
        code: string
    ): Observable<{ identityContinuation: string }> {
        return this.http.post<{ identityContinuation: string }>(`${this.publicUrl}/email-code/verify`, {
            continuation,
            code,
        });
    }

    public verifyLoginTotp$(continuation: string, code: string): Observable<StrongAuthLoginResponse> {
        return this.http.post<StrongAuthLoginResponse>(`${this.publicUrl}/login/totp/verify`, {
            continuation,
            code,
        });
    }

    public startLoginTotpEnrollment$(continuation: string): Observable<TotpEnrollmentResponse> {
        return this.http.post<TotpEnrollmentResponse>(`${this.publicUrl}/login/totp/enrollment/options`, {
            continuation,
        });
    }

    public confirmLoginTotpEnrollment$(
        loginContinuation: string,
        enrollmentContinuation: string,
        code: string
    ): Observable<StrongAuthLoginResponse> {
        return this.http.post<StrongAuthLoginResponse>(`${this.publicUrl}/login/totp/enrollment/verify`, {
            loginContinuation,
            enrollmentContinuation,
            code,
        });
    }

    public getLoginPasskeyOptions$(continuation: string): Observable<PasskeyAuthenticationOptionsResponse> {
        return this.http.post<PasskeyAuthenticationOptionsResponse>(`${this.publicUrl}/login/passkey/options`, {
            continuation,
        });
    }

    public verifyLoginPasskey$(
        loginContinuation: string,
        passkeyContinuation: string,
        response: AuthenticationResponseJSON
    ): Observable<StrongAuthLoginResponse> {
        return this.http.post<StrongAuthLoginResponse>(`${this.publicUrl}/login/passkey/verify`, {
            loginContinuation,
            passkeyContinuation,
            response,
        });
    }

    public startLoginPasskeyEnrollment$(
        loginContinuation: string,
        name: string
    ): Observable<PasskeyRegistrationOptionsResponse> {
        return this.http.post<PasskeyRegistrationOptionsResponse>(
            `${this.publicUrl}/login/passkey/enrollment/options`,
            { loginContinuation, name }
        );
    }

    public confirmLoginPasskeyEnrollment$(
        loginContinuation: string,
        enrollmentContinuation: string,
        response: RegistrationResponseJSON
    ): Observable<StrongAuthLoginResponse> {
        return this.http.post<StrongAuthLoginResponse>(`${this.publicUrl}/login/passkey/enrollment/verify`, {
            loginContinuation,
            enrollmentContinuation,
            response,
        });
    }

    public getCredentials$(): Observable<UserSecurityCredentials> {
        return this.http.get<UserSecurityCredentials>(`${this.authenticatedUrl}/credentials`);
    }

    public verifyFreshTotp$(code: string): Observable<{ continuation: string }> {
        return this.http.post<{ continuation: string }>(`${this.authenticatedUrl}/fresh/totp`, { code });
    }

    public getFreshPasskeyOptions$(): Observable<PasskeyAuthenticationOptionsResponse> {
        return this.http.post<PasskeyAuthenticationOptionsResponse>(
            `${this.authenticatedUrl}/fresh/passkey/options`,
            {}
        );
    }

    public verifyFreshPasskey$(
        passkeyContinuation: string,
        response: AuthenticationResponseJSON
    ): Observable<{ continuation: string }> {
        return this.http.post<{ continuation: string }>(`${this.authenticatedUrl}/fresh/passkey/verify`, {
            passkeyContinuation,
            response,
        });
    }

    public startTotpEnrollment$(freshContinuation?: string): Observable<TotpEnrollmentResponse> {
        return this.http.post<TotpEnrollmentResponse>(`${this.authenticatedUrl}/totp/enrollment/options`, {
            freshContinuation,
        });
    }

    public confirmTotpEnrollment$(
        enrollmentContinuation: string,
        code: string
    ): Observable<UserSecurityCredentials> {
        return this.http.post<UserSecurityCredentials>(`${this.authenticatedUrl}/totp/enrollment/verify`, {
            enrollmentContinuation,
            code,
        });
    }

    public startPasskeyEnrollment$(
        name: string,
        freshContinuation?: string
    ): Observable<PasskeyRegistrationOptionsResponse> {
        return this.http.post<PasskeyRegistrationOptionsResponse>(
            `${this.authenticatedUrl}/passkey/enrollment/options`,
            { name, freshContinuation }
        );
    }

    public confirmPasskeyEnrollment$(
        enrollmentContinuation: string,
        response: RegistrationResponseJSON
    ): Observable<UserSecurityCredentials> {
        return this.http.post<UserSecurityCredentials>(`${this.authenticatedUrl}/passkey/enrollment/verify`, {
            enrollmentContinuation,
            response,
        });
    }

    public removeTotp$(freshContinuation: string): Observable<UserSecurityCredentials> {
        return this.http.request<UserSecurityCredentials>('DELETE', `${this.authenticatedUrl}/totp`, {
            body: { freshContinuation },
        });
    }

    public removePasskey$(
        credentialId: string,
        freshContinuation: string
    ): Observable<UserSecurityCredentials> {
        return this.http.request<UserSecurityCredentials>(
            'DELETE',
            `${this.authenticatedUrl}/passkeys/${encodeURIComponent(credentialId)}`,
            { body: { freshContinuation } }
        );
    }

    public getOrganizationCapabilities$(organizationId: string): Observable<{ managementAvailable: boolean }> {
        return this.http.get<{ managementAvailable: boolean }>(
            `${this.authenticatedUrl.replace('/security', '')}/organization/${organizationId}/security/capabilities`
        );
    }

    public getOrganizationMembers$(organizationId: string): Observable<OrganizationMemberSecurityStatus[]> {
        return this.http.get<OrganizationMemberSecurityStatus[]>(
            `${this.authenticatedUrl.replace('/security', '')}/organization/${organizationId}/security/members`
        );
    }

    public updateOrganizationPolicy$(
        organizationId: string,
        policy: Partial<OrganizationSecurityPolicy>
    ): Observable<{ security: OrganizationSecurityPolicy }> {
        return this.http.put<{ security: OrganizationSecurityPolicy }>(
            `${this.authenticatedUrl.replace('/security', '')}/organization/${organizationId}/security`,
            policy
        );
    }

    public grantOrganizationExemption$(
        organizationId: string,
        userId: string,
        reason: string,
        durationHours = 24
    ): Observable<unknown> {
        return this.http.post(
            `${this.authenticatedUrl.replace('/security', '')}/organization/${organizationId}/security/exemptions`,
            { userId, reason, durationHours }
        );
    }

    public revokeOrganizationExemption$(organizationId: string, exemptionId: string): Observable<unknown> {
        return this.http.delete(
            `${this.authenticatedUrl.replace('/security', '')}/organization/${organizationId}/security/exemptions/${exemptionId}`
        );
    }
}
